/*eslint max-lines-per-function: ["error", 220]*/
import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { CreateUpdateBlockadeMonthExceptionDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import { Option } from '../../components/Selects/Selector';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { NewDatePickerReact } from '../../../app/components/NewDatePickerReact';
import ProjectSelector from '../Selects/ProjectSelector';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { NewDatePicker } from '../../../app/components/NewDatePicker';
import DynamicTextarea from '../../../app/components/DynamicTextarea';

interface BlockadeMonthExceptionEditorProps {
  id?: string;
  blockadeMonthException: CreateUpdateBlockadeMonthExceptionDto;
  handleClose?: () => void;
  handleCloseAndRefresh?: () => void;
  show?: boolean;
  readonly?: boolean;
  onCompleted: (
    blockadeMonthException: CreateUpdateBlockadeMonthExceptionDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const BlockadeMonthExceptionEditor: React.FC<
  BlockadeMonthExceptionEditorProps
> = props => {
  const [blockadeMonthException, setBlockadeMonthException] = useState(
    props.blockadeMonthException,
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('projectLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setBlockadeMonthException(props.blockadeMonthException);
    }
  }, [props.blockadeMonthException, props.show]);

  const createBlockadeMonthException = async () => {
    setIsLoading(true);
    props
      .onCompleted(blockadeMonthException, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (
    obj: Partial<CreateUpdateBlockadeMonthExceptionDto>,
  ) => {
    setBlockadeMonthException({ ...blockadeMonthException, ...obj });
  };

  const setDate = (date: Date | undefined) => {
    if (date) {
      //ten kalendarz inaczej ustawia godzinę niż nasz główny kalendarz
      //dlatego trzeba skorygować godzinę
      date.setMinutes(-date.getTimezoneOffset());
      return date;
    }
    return undefined;
  };

  const deactivateBlockadeException = async () => {
    if (props.id) {
      pok.blockadesException
        .deactivate(props.id)
        .then(
          () => props.handleCloseAndRefresh && props?.handleCloseAndRefresh(),
        )
        .catch(async errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.readonly
              ? t(tk.projects.viewMonthBlockadeException)
              : props.id
                ? t(tk.projects.editMonthBlockadeException)
                : t(tk.projects.addMonthBlockadeException)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="d-grid gap-3">
            <FormRow controlId="project" label={t(tk.prLang.project)}>
              <ProjectSelector
                onChange={option =>
                  propertyChange({ projectId: (option as Option).value })
                }
                value={props.blockadeMonthException?.projectId}
                readOnly={props.readonly}
              />
            </FormRow>
            <FormRow controlId="isSalesBlockade" label={t(tk.common.month)}>
              <NewDatePickerReact
                onChange={value =>
                  propertyChange({ date: setDate(value as Date) })
                }
                value={blockadeMonthException.date}
                disabled={props.readonly}
              />
            </FormRow>
            <FormRow
              controlId="isSalesUnblockade"
              label={t(tk.projects.salesUnblockade)}
            >
              <Form.Check
                type="checkbox"
                className="pt-2"
                checked={blockadeMonthException?.isSalesUnblocked ?? true}
                onChange={e =>
                  propertyChange({ isSalesUnblocked: e.target.checked })
                }
                disabled={props.readonly}
              />
            </FormRow>
            <FormRow
              controlId="isPurchaseUnblockade"
              label={t(tk.projects.purchaseUnblockade)}
            >
              <Form.Check
                type="checkbox"
                className="pt-2"
                checked={blockadeMonthException?.isPurchaseUnblocked ?? true}
                onChange={e =>
                  propertyChange({ isPurchaseUnblocked: e.target.checked })
                }
                disabled={props.readonly}
              />
            </FormRow>
            <FormRow
              controlId="dateToDelete"
              label={t(tk.projects.dateToDeleteBlockade)}
            >
              <NewDatePicker
                value={blockadeMonthException.dateToDelete}
                dateChanged={value =>
                  propertyChange({ dateToDelete: value as Date })
                }
                readOnly={props.readonly}
              />
            </FormRow>
            <FormRow
              controlId="reason"
              label={t(tk.projects.addExceptionReason)}
            >
              <DynamicTextarea
                value={blockadeMonthException.reason ?? ''}
                onChange={e =>
                  propertyChange({
                    reason: e.target.value || undefined,
                  })
                }
                readOnly={props.readonly}
              />
            </FormRow>
          </Form>
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={pok.blockadesException.getLastEditor}
            />
          )}
          {props.id && props.blockadeMonthException.active && (
            <ConfirmationButton
              onOK={deactivateBlockadeException}
              confirmation={t(tk.projects.askDeleteBlockadeException)}
              variant="outline-danger"
            >
              {t(tk.button.delete)}
            </ConfirmationButton>
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          {!props.readonly && (
            <Button
              variant="primary"
              onClick={createBlockadeMonthException}
              isLoading={isLoading}
            >
              {t(tk.button.save)}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
